import * as React from "react";
import { useEffect, useState } from "react";
import { fetchRegions, fetchCitiesByRegion, fetchCentersByCity } from "../../../services/geoService";

export type FilterState = {
  regionId: number | null;
  cityId: number | null;
  centerId: number | null;
  minPrice: number | null;
  maxPrice: number | null;
  searchKeyword?: string;
};

type Props = {
  initial: FilterState;
  onApply: (f: FilterState) => void;
};

type Option = { id: number; name: string };

const parseNum = (v: string): number | null => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toOption = (row: any): Option => ({
  id: row?.id ?? row?.Id ?? 0,
  name: row?.name ?? row?.Name ?? "",
});

const baseRing = "focus:ring-[rgba(6,118,71,0.18)]";
const baseBorder = "border-[rgba(45,80,22,0.12)]";
const hoverBorder = "hover:border-[#067647]";
const focusBorder = "focus:border-[#067647]";

function selectClass(disabled = false): string {
  return [
    "appearance-none bg-none",
    "-webkit-appearance-none",
    "-moz-appearance-none",
    "w-full py-2 pr-3 pl-9 rounded-xl border-2 bg-white/95 text-gray-900 text-[0.8rem] shadow-sm outline-none transition",
    "[background-image:none!important]",
    "[background-repeat:no-repeat!important]",
    "[background-position:unset!important]",
    "[background-size:unset!important]",
    "[background-origin:unset!important]",
    "[background-clip:unset!important]",
    "[padding-inline-end:0.75rem!important]",
    "[padding-inline-start:2.25rem!important]",
    baseBorder,
    "hover:bg-white hover:-translate-y-[1px]",
    "focus:bg-white focus:-translate-y-[2px] focus:shadow-lg",
    baseRing,
    hoverBorder,
    focusBorder,
    disabled ? "opacity-60 pointer-events-none cursor-not-allowed" : "",
  ].join(" ");
}

const LandFilters: React.FC<Props> = ({ initial, onApply }) => {
  // Set Arabic and RTL on mount
  useEffect(() => {
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
  }, []);

  const [form, setForm] = useState<FilterState>(() => ({ ...initial }));

  const [regions, setRegions] = useState<Option[]>([]);
  const [cities, setCities] = useState<Option[]>([]);
  const [centers, setCenters] = useState<Option[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingCenters, setLoadingCenters] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingRegions(true);
      try {
        const rows = await fetchRegions();
        if (!alive) return;
        setRegions((rows ?? []).map(toOption));
      } finally {
        if (alive) setLoadingRegions(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    if (!form.regionId) {
      setCities([]); setCenters([]);
      if (form.cityId || form.centerId) setForm(f => ({ ...f, cityId: null, centerId: null }));
      return;
    }
    (async () => {
      setLoadingCities(true);
      try {
        const rows = await fetchCitiesByRegion(form.regionId!);
        if (!alive) return;
        const opts = (rows ?? []).map(toOption);
        setCities(opts);
        if (form.cityId && !opts.some(c => c.id === form.cityId)) {
          setForm(f => ({ ...f, cityId: null, centerId: null }));
        } else {
          setCenters([]); if (form.centerId) setForm(f => ({ ...f, centerId: null }));
        }
      } finally {
        if (alive) setLoadingCities(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.regionId]);

  useEffect(() => {
    let alive = true;
    if (!form.cityId) { setCenters([]); if (form.centerId) setForm(f => ({ ...f, centerId: null })); return; }
    (async () => {
      setLoadingCenters(true);
      try {
        const rows = await fetchCentersByCity(form.cityId!);
        if (!alive) return;
        const opts = (rows ?? []).map(toOption);
        setCenters(opts);
        if (form.centerId && !opts.some(c => c.id === form.centerId)) {
          setForm(f => ({ ...f, centerId: null }));
        }
      } finally {
        if (alive) setLoadingCenters(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cityId]);

  const onChange = (patch: Partial<FilterState>) => setForm(prev => ({ ...prev, ...patch }));

  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    onApply(form);
  };

    const reset = () => {
    const cleared: FilterState = {
      regionId: null,
      cityId: null,
      centerId: null,
      minPrice: null,
      maxPrice: null,
      searchKeyword: "",
    };
    setForm(cleared);
    setCities([]);
    setCenters([]);
    onApply(cleared);
  };

  const inputClass = (disabled = false): string => {
    return [
      "w-full py-2 pr-8 pl-3 rounded-xl border-2 bg-white/95 text-gray-900 text-[0.8rem] shadow-sm outline-none transition",
      baseBorder, "hover:bg-white hover:-translate-y-[1px]",
      "focus:bg-white focus:-translate-y-[2px] focus:shadow-lg",
      baseRing, hoverBorder, focusBorder,
      disabled ? "opacity-60 pointer-events-none cursor-not-allowed" : "",
    ].join(" ");
  };

  return (
    <div className="modern-filters-container">
      <form onSubmit={apply} className="modern-filters-form">
        {/* All filters in one line */}
        <div className="flex items-end gap-2 flex-wrap">
          {/* Region */}
          <div className="filter-group flex-1 min-w-[140px]">
            <label className="filter-label font-arabic text-xs mb-1">
              المنطقة
            </label>
            <div className="relative flex items-center">
              <span aria-hidden className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-20">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
              </span>
              <select
                className={selectClass(false)}
                value={form.regionId ?? ""}
                data-has-value={Boolean(form.regionId)}
                onChange={(e) => onChange({ regionId: parseNum(e.target.value) })}
                aria-label="اختيار المنطقة"
                style={{
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none',
                  backgroundImage: 'none',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'unset',
                  backgroundSize: 'unset',
                  backgroundOrigin: 'unset',
                  backgroundClip: 'unset',
                  paddingInlineEnd: '0.75rem',
                  paddingInlineStart: '2.25rem'
                }}
              >
                <option value="">اختر المنطقة</option>
                {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          </div>

          {/* City */}
          <div className="filter-group flex-1 min-w-[140px]">
            <label className="filter-label font-arabic text-xs mb-1">
              المدينة
            </label>
            <div className="relative flex items-center">
              <span aria-hidden className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-20">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
              </span>
              <select
                className={selectClass(!form.regionId || loadingCities)}
                value={form.cityId ?? ""}
                data-has-value={Boolean(form.cityId)}
                onChange={(e) => onChange({ cityId: parseNum(e.target.value) })}
                disabled={!form.regionId || loadingCities}
                aria-label="اختيار المدينة"
                style={{
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none',
                  backgroundImage: 'none',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'unset',
                  backgroundSize: 'unset',
                  backgroundOrigin: 'unset',
                  backgroundClip: 'unset',
                  paddingInlineEnd: '0.75rem',
                  paddingInlineStart: '2.25rem'
                }}
              >
                <option value="">
                  {loadingCities ? "جاري التحميل…" : "اختر المدينة"}
                </option>
                {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          {/* Center */}
          <div className="filter-group flex-1 min-w-[140px]">
            <label className="filter-label font-arabic text-xs mb-1">
              المركز <span className="optional-badge text-[0.65rem] px-1.5 py-0.5">اختياري</span>
            </label>
            <div className="relative flex items-center">
              <span aria-hidden className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-20">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
              </span>
              <select
                className={selectClass(!form.cityId || loadingCenters)}
                value={form.centerId ?? ""}
                data-has-value={Boolean(form.centerId)}
                onChange={(e) => onChange({ centerId: parseNum(e.target.value) })}
                disabled={!form.cityId || loadingCenters}
                aria-label="اختيار المركز"
                style={{
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none',
                  backgroundImage: 'none',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'unset',
                  backgroundSize: 'unset',
                  backgroundOrigin: 'unset',
                  backgroundClip: 'unset',
                  paddingInlineEnd: '0.75rem',
                  paddingInlineStart: '2.25rem'
                }}
              >
                <option value="">
                  {loadingCenters ? "جاري التحميل…" : "مركز (اختياري)"}
                </option>
                {centers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          {/* Min price */}
          <div className="filter-group flex-1 min-w-[140px]">
            <label className="filter-label font-arabic text-xs mb-1">
              الحد الأدنى
            </label>
            <div className="relative flex items-center">
              <span aria-hidden className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none z-20">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
              </span>
              <input
                type="number"
                className={inputClass(false)}
                placeholder="مثال: 50000"
                value={form.minPrice ?? ""}
                onChange={(e) => onChange({ minPrice: parseNum(e.target.value) })}
                min={0}
                inputMode="numeric"
              />
            </div>
          </div>

          {/* Max price */}
          <div className="filter-group flex-1 min-w-[140px]">
            <label className="filter-label font-arabic text-xs mb-1">
              الحد الأقصى
            </label>
            <div className="relative flex items-center">
              <span aria-hidden className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none z-20">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
              </span>
              <input
                type="number"
                className={inputClass(false)}
                placeholder="مثال: 2000000"
                value={form.maxPrice ?? ""}
                onChange={(e) => onChange({ maxPrice: parseNum(e.target.value) })}
                min={0}
                inputMode="numeric"
              />
            </div>
          </div>

          {/* District / Sequence Number */}
          <div className="filter-group flex-1 min-w-[180px]">
            <label className="filter-label font-arabic text-xs mb-1">
              الحي أو الرقم التسلسلي
            </label>
            <div className="relative flex items-center">
              <span
                aria-hidden
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none z-20"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </span>

              <input
                type="text"
                className={inputClass(false)}
                placeholder="الحي أو الرقم التسلسلي"
                value={form.searchKeyword ?? ""}
                onChange={(e) => onChange({ searchKeyword: e.target.value })}
                maxLength={100}
              />
            </div>
          </div>

          {/* Action Buttons - Inline with filters */}
          <button
            type="button"
            onClick={reset}
            className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
            مسح
          </button>
          <button
            type="submit"
            disabled={loadingRegions}
            className="px-4 py-2 bg-sa-600 text-white rounded-lg hover:bg-sa-700 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed h-[42px]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            بحث
          </button>
        </div>
      </form>
    </div>
  );
};

export default LandFilters;
